import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FaTwitter,
  FaInstagram,
  FaFacebookF,
  FaYoutube,
  FaTiktok,
  FaTelegramPlane,
  FaSearch,
  FaRegClock,
  FaThLarge,
  FaGift,
  FaTrophy,
  FaGamepad,
  FaTags,
} from "react-icons/fa";

import promoFirst from "../../images/blog/new-images/First.jpg";
import promoClaim from "../../images/blog/new-images/ClaimDeposit.jpg";
import promoVip from "../../images/blog/new-images/VIP.jpg";
import promoSunday from "../../images/blog/new-images/SundayBonus.jpg";
import promoLogin from "../../images/blog/new-images/spinlogin.jpg";

// Popular casino games — image + name, linking to the play site (same require() pattern
// the homepage uses, which handles the spaces in these filenames).
const POPULAR_GAMES = [
  { name: "Adrenaline",  img: require("../../images/blog/games/Adrenaline.jpg") },
  { name: "Plinko",      img: require("../../images/blog/games/Plinko.jpg") },
  { name: "Super Coins", img: require("../../images/blog/games/Super Coins.jpg") },
  { name: "Tiger Shrine", img: require("../../images/blog/games/Tiger shrine.jpg") },
  { name: "Money Minter", img: require("../../images/blog/games/Money Minter.jpg") },
  { name: "Big Catch",   img: require("../../images/blog/games/Big Catch.jpg") },
];

const PLAY_BASE = "https://coin.spinsagacasino.com";
const SIGNUP_URL = `${PLAY_BASE}/signUp`;

// Static image imports may resolve to an object ({ src }) or a string
// depending on the loader — this handles both.
const src = (img) => (img && img.src) || img;

const SOCIALS = [
  { label: "X",         href: "https://x.com/SpinSagaCasino",                         Icon: FaTwitter },
  { label: "Instagram", href: "https://www.instagram.com/spinsagacasino/",            Icon: FaInstagram },
  { label: "Facebook",  href: "https://www.facebook.com/profile.php?id=61563257325711", Icon: FaFacebookF },
  { label: "YouTube",   href: "https://www.youtube.com/@PlaySpinSaga",                Icon: FaYoutube },
  { label: "TikTok",    href: "https://www.tiktok.com/@user8pcdfmv77d",               Icon: FaTiktok },
  { label: "Telegram",  href: "https://t.me/CoinSpinSaga",                            Icon: FaTelegramPlane },
];

function thumbOf(p) {
  // Prefer the article's hero image (unique per post) over the shared promo thumbnail.
  return p?.heroImageUrl || p?.thumbnailUrl || null;
}

function dateOf(p) {
  return p?.author?.publishedDate || p?.publishedDate || p?.createdAt || p?.approvedAt || null;
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function PostItem({ post }) {
  const thumb = thumbOf(post);
  return (
    <Link to={`/blog/${post.slug}`} className="ss-postitem">
      {thumb ? (
        <img src={thumb} alt="" loading="lazy" />
      ) : (
        <span className="ss-postitem__ph" aria-hidden="true" />
      )}
      <span>
        <span className="ss-postitem__title">{post.title}</span>
        <span className="ss-postitem__date">{formatDate(dateOf(post))}</span>
      </span>
    </Link>
  );
}

export default function BlogSidebar({ pages = [], current }) {
  const { t } = useTranslation();
  const currentSlug = current?.slug;

  const others = useMemo(
    () => (Array.isArray(pages) ? pages.filter((p) => p && p.slug !== currentSlug) : []),
    [pages, currentSlug]
  );

  // Recent — newest first.
  const recent = useMemo(
    () =>
      [...others]
        .sort((a, b) => new Date(dateOf(b) || 0) - new Date(dateOf(a) || 0))
        .slice(0, 4),
    [others]
  );

  // Editor's Picks — highest content quality score (free stand-in for "Most Popular",
  // which would need view tracking the API does not have).
  const picks = useMemo(
    () =>
      [...others]
        .sort((a, b) => (b?.qualityScore || 0) - (a?.qualityScore || 0))
        .slice(0, 3),
    [others]
  );

  // Browse-by-topic tabs from the most common tags.
  const topics = useMemo(() => {
    const counts = {};
    others.forEach((p) =>
      (p.tags || []).forEach((t) => {
        const name = t?.name;
        if (name) counts[name] = (counts[name] || 0) + 1;
      })
    );
    return Object.keys(counts)
      .sort((a, b) => counts[b] - counts[a])
      .slice(0, 5);
  }, [others]);

  const [activeTopic, setActiveTopic] = useState(null);
  const topic = activeTopic || topics[0] || null;
  const topicPosts = useMemo(() => {
    if (!topic) return [];
    return others
      .filter((p) => (p.tags || []).some((t) => t?.name === topic))
      .slice(0, 4);
  }, [others, topic]);

  // All unique tags across posts — for the tag cloud.
  const allTags = useMemo(() => {
    const seen = new Map();
    (Array.isArray(pages) ? pages : []).forEach((p) =>
      (p.tags || []).forEach((t) => {
        if (t?.name && !seen.has(t.name)) seen.set(t.name, t);
      })
    );
    return Array.from(seen.values()).slice(0, 12);
  }, [pages]);

  // Search — client-side filter over titles.
  const [query, setQuery] = useState("");
  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    return others.filter((p) => (p.title || "").toLowerCase().includes(q)).slice(0, 6);
  }, [others, query]);

  const CASINO_TILES = [
    { img: promoClaim, label: t('blogSidebar.depositMatch') },
    { img: promoVip, label: t('blogSidebar.vipRewards') },
    { img: promoSunday, label: t('blogSidebar.sundayBonus') },
    { img: promoLogin, label: t('blogSidebar.dailyLogin') },
  ];

  return (
    <aside className="ss-blog__aside">
      {/* Follow SpinSaga (top) */}
      <div className="ss-widget">
        <h3 className="ss-widget__title">
          <span className="accent">{t('blogSidebar.weareSocialAccent')}</span> {t('blogSidebar.weareSocialRest')}
        </h3>
        <div className="ss-social">
          {SOCIALS.map(({ label, href, Icon }) => (
            <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label}>
              <Icon />
            </a>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="ss-widget">
        <div className="ss-search">
          <input
            type="text"
            placeholder={t('blogSidebar.searchPlaceholder')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label={t('blogSidebar.searchLabel')}
          />
          <button type="button" className="ss-search__btn" aria-label={t('blogSidebar.searchLabel')}>
            <FaSearch />
          </button>
        </div>
        {query.trim() && (
          <div className="ss-search__results">
            {results.length ? (
              results.map((p) => (
                <Link key={p.slug} to={`/blog/${p.slug}`}>
                  {p.title}
                </Link>
              ))
            ) : (
              <p className="ss-search__empty">{t('blogSidebar.noResults', { query })}</p>
            )}
          </div>
        )}
      </div>

      {/* Recent posts */}
      {recent.length > 0 && (
        <div className="ss-widget">
          <h3 className="ss-widget__title">
            <FaRegClock /> {t('blogSidebar.recentPosts')}
          </h3>
          <div className="ss-postlist">
            {recent.map((p) => (
              <PostItem key={p.slug} post={p} />
            ))}
          </div>
        </div>
      )}

      {/* Editor's picks */}
      {picks.length > 0 && (
        <div className="ss-widget">
          <h3 className="ss-widget__title">
            <FaTrophy /> {t('blogSidebar.editorsPicks')}
          </h3>
          <div className="ss-postlist">
            {picks.map((p) => (
              <PostItem key={p.slug} post={p} />
            ))}
          </div>
        </div>
      )}

      {/* Tags — directly below Editor's Picks */}
      {allTags.length > 0 && (
        <div className="ss-widget">
          <h3 className="ss-widget__title">
            <FaTags /> {t('blogSidebar.tags')}
          </h3>
          <div className="ss-tagcloud">
            {allTags.map((t) => (
              <Link key={t.slug || t.name} to="/blog">
                #{t.name}
              </Link>
            ))}
          </div>
        </div>
      )}

      {/* Claim bonus CTA */}
      <a className="ss-cta" href={SIGNUP_URL} target="_blank" rel="noopener noreferrer">
        <img src={src(promoFirst)} alt="Claim your free welcome bonus on signup" />
        <span className="ss-cta__body">
          <span className="ss-cta__btn">{t('blogSidebar.signUpNow')}</span>
        </span>
      </a>

      {/* Browse by topic */}
      {topics.length > 0 && (
        <div className="ss-widget">
          <h3 className="ss-widget__title">
            <FaThLarge /> {t('blogSidebar.browseByTopic')}
          </h3>
          <div className="ss-tabs">
            {topics.map((t) => (
              <button
                key={t}
                type="button"
                className={`ss-tab ${t === topic ? "is-active" : ""}`}
                onClick={() => setActiveTopic(t)}
              >
                {t}
              </button>
            ))}
          </div>
          <div className="ss-postlist">
            {topicPosts.length ? (
              topicPosts.map((p) => <PostItem key={p.slug} post={p} />)
            ) : (
              <p className="ss-search__empty">{t('blogSidebar.noTopicPosts')}</p>
            )}
          </div>
        </div>
      )}

      {/* From the casino */}
      <div className="ss-widget">
        <h3 className="ss-widget__title">
          <FaGift /> {t('blogSidebar.fromCasino')}
        </h3>
        <div className="ss-casino-grid">
          {CASINO_TILES.map((tile) => (
            <a
              key={tile.label}
              className="ss-casino-tile"
              href={SIGNUP_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={src(tile.img)} alt={tile.label} loading="lazy" />
              <span className="ss-casino-tile__name">{tile.label}</span>
            </a>
          ))}
        </div>
      </div>

      {/* Most Popular Games (last) */}
      <div className="ss-widget">
        <h3 className="ss-widget__title">
          <FaGamepad /> {t('blogSidebar.popularGames')}
        </h3>
        <div className="ss-games">
          {POPULAR_GAMES.map((g) => (
            <a
              key={g.name}
              className="ss-game"
              href={PLAY_BASE}
              target="_blank"
              rel="noopener noreferrer"
            >
              <img src={src(g.img)} alt={`${g.name} slot game`} loading="lazy" />
              <span className="ss-game__name">{g.name}</span>
            </a>
          ))}
        </div>
      </div>
    </aside>
  );
}
